import React from 'react';
import { Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { MODES } from '../hooks/useTimer';

const MotionBox = motion(Box);

const modeOrbScale = {
  [MODES.FOCUS]: { s: 1, o: 1 },
  [MODES.SHORT_BREAK]: { s: 0.92, o: 0.85 },
  [MODES.LONG_BREAK]: { s: 1.08, o: 0.78 },
};

const AmbientBackground = ({ theme, isRunning, mode }) => {
  const tune = modeOrbScale[mode] || modeOrbScale[MODES.FOCUS];

  return (
    <Box
      position="absolute"
      inset={0}
      overflow="hidden"
      className="fl-grain"
      zIndex={0}
    >
      <Box
        position="absolute"
        inset={0}
        style={{ background: theme.bg, transition: 'background 1.4s ease' }}
      />

      {/* Slow drifting light wash — center-weighted */}
      <MotionBox
        position="absolute"
        inset="-15%"
        style={{
          background: `radial-gradient(ellipse 50% 45% at 45% 35%, ${theme.focusAccent}14 0%, transparent 55%)`,
        }}
        animate={{
          x: ['-3%', '4%', '-2%', '0%'],
          y: ['-2%', '3%', '-1%', '0%'],
          opacity: isRunning ? [0.55, 0.85, 0.65, 0.55] : [0.35, 0.45, 0.38, 0.35],
        }}
        transition={{
          duration: mode === MODES.FOCUS ? 32 : mode === MODES.SHORT_BREAK ? 40 : 48,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Counter-drift wash — softer, break-friendly */}
      <MotionBox
        position="absolute"
        inset="-20%"
        style={{
          background: (() => {
            if (mode === MODES.FOCUS) {
              return `radial-gradient(ellipse 42% 38% at 58% 62%, ${theme.focusAccent}0a 0%, transparent 50%)`;
            }
            const c = mode === MODES.LONG_BREAK ? theme.longBreakAccent : theme.breakAccent;
            return `radial-gradient(ellipse 48% 42% at 55% 58%, ${c}12 0%, transparent 52%)`;
          })(),
        }}
        animate={{
          x: ['2%', '-3%', '1%', '0%'],
          y: ['1%', '-2%', '0.5%', '0%'],
          scale: mode === MODES.FOCUS ? [1, 1.03, 1.01, 1] : [1, 1.06, 1.02, 1],
          opacity: isRunning
            ? (mode === MODES.FOCUS ? [0.35, 0.55, 0.42, 0.35] : [0.45, 0.62, 0.5, 0.45])
            : [0.22, 0.32, 0.26, 0.22],
        }}
        transition={{
          duration: mode === MODES.LONG_BREAK ? 56 : mode === MODES.SHORT_BREAK ? 44 : 38,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {theme.orbs.map((color, i) => (
        <MotionBox
          key={i}
          className={['fl-orb-a', 'fl-orb-b', 'fl-orb-c'][i]}
          position="absolute"
          borderRadius="full"
          style={{
            background: color,
            filter: 'blur(80px)',
            width: ['480px', '360px', '300px'][i],
            height: ['480px', '360px', '300px'][i],
            top: theme.orbPositions[i].split(' ')[1],
            left: theme.orbPositions[i].split(' ')[0],
            transform: 'translate(-50%, -50%)',
            transition: 'background 1.2s ease',
          }}
          animate={
            isRunning && i === 0
              ? {
                  scale: [tune.s, tune.s * 1.03, tune.s * 0.98, tune.s],
                  opacity: [0.82 * tune.o, 1 * tune.o, 0.88 * tune.o, 0.82 * tune.o],
                }
              : { scale: tune.s, opacity: 0.9 * tune.o }
          }
          transition={{
            duration: mode === MODES.FOCUS ? 5.5 : mode === MODES.SHORT_BREAK ? 7 : 9,
            repeat: isRunning && i === 0 ? Infinity : 0,
            ease: 'easeInOut',
          }}
        />
      ))}

      <Box
        position="absolute"
        inset={0}
        style={{
          background: mode === MODES.FOCUS
            ? 'radial-gradient(ellipse 58% 58% at 50% 48%, transparent 36%, rgba(0,0,0,0.58) 100%)'
            : 'radial-gradient(ellipse 62% 62% at 50% 48%, transparent 42%, rgba(0,0,0,0.48) 100%)',
          transition: 'background 1.2s ease',
        }}
      />
    </Box>
  );
};

export default AmbientBackground;
